<template>
  <section class="enquiries-wrapper">
    <div class="container-lg">
      <div class="row justify-content-center">
        <div class="col-xl-7 col-lg-8">
          <!-- Page header -->
          <div class="page-bar py-3 mb-4">
            <div class="d-flex align-items-center">
              <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
                <i class="fas fa-chevron-left"></i>
              </BackButton>
              <div class="page-title flex-grow-1 text-center">Enquiries List</div>
            </div>
          </div>

          <div class="enquiry-list">
            <div v-if="loadingList" class="enquiry-skeleton">
              <div class="skeleton-card" v-for="n in 3" :key="n">
                <div class="skeleton-avatar"></div>
                <div class="skeleton-lines">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>

            <div v-else-if="!enquiries.length" class="enquiry-empty text-center">
              <img src="/assets/empty-state.svg" alt="No enquiries" />
              <h5>No enquiries yet</h5>
              <p class="text-muted mb-3">
                When you send enquiries to vendors, they will appear here for quick follow-up.
              </p>
              <router-link to="/vendors" class="btn btn-outline-primary btn-sm">
                Explore Vendors
              </router-link>
            </div>

            <article
              v-else
              v-for="enquiry in enquiries"
              :key="enquiry.id"
              class="enquiry-card"
              :class="{ disabled: Number(enquiry.eventid) === 0 }"
              @click="handleEnquiryClick(enquiry)"
            >
              <div class="enquiry-card__media">
                <img
                  :src="enquiry.s_user_img || enquiry.event_image || 'https://dummyimage.com/200x200/edf2f7/94a3b8.png&text=ENQ'"
                  :alt="enquiry.s_business || enquiry.event_name"
                  loading="lazy"
                />
              </div>
              <div class="enquiry-card__body">
                <div class="enquiry-card__title">
                  <h4>{{ enquiry.s_business || enquiry.event_name }}</h4>
                  <span class="badge badge-pill badge-soft">
                    {{ enquiry.contextual_categories || 'General' }}
                  </span>
                </div>
                <p class="enquiry-card__desc">
                  {{ enquiry.description || 'No description provided.' }}
                </p>
                <div class="enquiry-meta">
                  <div class="meta-item">
                    <i class="fas fa-calendar-alt"></i>
                    <span>{{ formatDate(enquiry.created_at) }}</span>
                  </div>
                  <div class="meta-item" v-if="enquiry.city || enquiry.location">
                    <i class="fas fa-map-marker-alt"></i>
                    <span>{{ enquiry.city || enquiry.location }}</span>
                  </div>
                </div>
              </div>
              <div class="enquiry-card__action">
                <i class="fas fa-chevron-right"></i>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div v-if="showModal" class="modal-backdrop-custom" @click.self="closeModal">
      <div class="modal-card">
        <div class="modal-header">
          <div>
            <p class="text-muted mb-1 small">Vendors List</p>
            <h5 class="modal-title">
              {{ enquiryDetailTitle }}
            </h5>
          </div>
          <button class="close" @click="closeModal" aria-label="Close">&times;</button>
        </div>

        <div class="modal-body">
          <div v-if="loadingDetail" class="text-center py-4">
            <div class="spinner-border text-primary"></div>
          </div>

          <div v-else-if="vendors.length === 0" class="text-center py-4 text-muted">
            No vendors found.
          </div>

          <div v-else>
            <div class="vendor-card" v-for="vendor in vendors" :key="vendor.s_id">
              <img
                :src="vendor.s_user_img || 'https://dummyimage.com/200x200/ccc/fff.png&text=No+Image'"
                alt="vendor"
                loading="lazy"
              />
              <div>
                <h5 class="mb-1">{{ vendor.s_business }}</h5>
                <p class="mb-1 small text-muted">
                  <i class="fas fa-phone text-primary mr-1"></i>
                  {{ vendor.s_phone || 'NA' }}
                </p>
                <p class="mb-1 small text-muted">
                  <i class="fas fa-envelope text-primary mr-1"></i>
                  {{ vendor.s_email || 'NA' }}
                </p>
                <p class="small mt-2 text-dark">
                  {{ vendor.selected_context_categories || 'No categories selected' }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer text-right">
          <button class="btn btn-outline-secondary btn-sm" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useSessionStore } from "@/stores/session";
import api from "@/services/api";
import { useHead } from "@vueuse/head";
import { formatDisplayDate } from '@/utils/date';
import BackButton from '@/components/common/BackButton.vue'

const session = useSessionStore();

useHead({
  title: "Enquiries - Invi8",
  meta: [
    { name: "description", content: "Enquiries list from Invi8." },
    { name: "keywords", content: "enquiries, invites, vendors, amplifier" },
  ],
});

const enquiries = ref([]);
const loadingList = ref(true);
const showModal = ref(false);
const loadingDetail = ref(false);
const vendors = ref([]);
const enquiryDetailTitle = ref("");

// Fetch main enquiries list
async function getEnquiries() {
  try {
    const formData = new FormData();
    formData.append("userid", session.userId);
    const response = await api.getEnquiries(formData);

    if (response?.data?.status === "ok") {
      enquiries.value = response.data.list || [];
    } else {
      enquiries.value = [];
    }
  } catch (e) {
    console.error("getEnquiries error:", e);
  } finally {
    loadingList.value = false;
  }
}

const formatDate = (dateStr) => formatDisplayDate(dateStr, { options: { month: 'short', day: '2-digit', year: 'numeric' } });

// When user clicks an enquiry
async function handleEnquiryClick(enquiry) {
  if (Number(enquiry.eventid) === 0) return; // skip if eventid = 0
  enquiryDetailTitle.value = enquiry.contextual_categories || "Vendors";
  showModal.value = true;
  await fetchEnquiryDetail(enquiry);
}

// Fetch details for a specific enquiry
async function fetchEnquiryDetail(enquiry) {
  try {
    loadingDetail.value = true;
    vendors.value = [];

    const formData = new FormData();
    formData.append("userid", session.userId);
    formData.append("eventid", enquiry.eventid);
    formData.append("enquiry_id", enquiry.id);

    const response = await api.getInviteEnquiryDetails(formData);
    if (response?.data?.status === "ok") {
      vendors.value = response.data.data || [];
    } else {
      vendors.value = [];
    }
  } catch (e) {
    console.error("getInviteEnquiryDetails error:", e);
  } finally {
    loadingDetail.value = false;
  }
}

function closeModal() {
  showModal.value = false;
  vendors.value = [];
}

onMounted(() => {
  getEnquiries();
});
</script>

<style scoped>
.enquiries-wrapper {
  padding-top: 2rem;
  padding-bottom: 3rem;
  overflow-x: hidden;
}

.enquiry-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.enquiry-card {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem;
  width: 100%;
  box-sizing: border-box;
  border-radius: 18px;
  border: 1px solid #eef2f7;
  background: #fff;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08);
  transition: transform .15s ease, box-shadow .15s ease;
  cursor: pointer;
}
.enquiry-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 15px 35px rgba(15, 23, 42, 0.12);
}
.enquiry-card.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.enquiry-card__media img {
  width: 62px;
  height: 62px;
  object-fit: cover;
  border-radius: 16px;
}

.enquiry-card__body {
  flex: 1;
  min-width: 0;
}
.enquiry-card__title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: .5rem;
  margin-bottom: .2rem;
}
.enquiry-card__title h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  word-break: break-word;
}
.enquiry-card__desc {
  margin: 0.25rem 0 0.5rem;
  color: #475569;
  font-size: 0.9rem;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}
.enquiry-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 0.85rem;
  color: #64748b;
}
.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
}
.meta-item i {
  color: #9ca3af;
}

.enquiry-card__action {
  color: #cbd5f5;
  align-self: center;
  font-size: 1.1rem;
}

.badge-soft {
  background: #f5f3ff;
  color: #6d28d9;
  font-size: 0.75rem;
}

.enquiry-skeleton .skeleton-card {
  display: flex;
  gap: 12px;
  padding: 1rem;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
}
.skeleton-avatar {
  width: 50px;
  height: 50px;
  border-radius: 16px;
  background: linear-gradient(90deg, #e6edf7 0%, #f5f8fc 50%, #e6edf7 100%);
  animation: shimmer 1.3s infinite;
}
.skeleton-lines span {
  display: block;
  height: 12px;
  width: 100%;
  margin-bottom: 8px;
  background: linear-gradient(90deg, #e6edf7 0%, #f5f8fc 50%, #e6edf7 100%);
  animation: shimmer 1.3s infinite;
}
.enquiry-empty img {
  max-width: 180px;
  margin-bottom: 1rem;
}

.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 1rem;
}
.modal-card {
  background: #fff;
  border-radius: 18px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: fadeIn 0.25s ease;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.2);
}
.modal-header,
.modal-footer {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #eef2f7;
}
.modal-footer {
  border-top: 1px solid #eef2f7;
}
.modal-body {
  padding: 1rem 1.25rem;
}
.close {
  background: rgba(15, 23, 42, 0.08);
  border: none;
  font-size: 1.2rem;
  line-height: 1;
  width: 36px;
  height: 36px;
  border-radius: 50%;
}
.vendor-card {
  display: flex;
  gap: 12px;
  padding: 0.9rem;
  border-radius: 14px;
  border: 1px solid #eef2f7;
  margin-bottom: 0.8rem;
}
.vendor-card img {
  width: 55px;
  height: 55px;
  object-fit: cover;
  border-radius: 14px;
}

@media (max-width: 991px) {
  .enquiries-wrapper {
    padding-top: 1.5rem;
    padding-bottom: 2rem;
  }
  .enquiry-card {
    grid-template-columns: 56px 1fr;
    grid-template-rows: auto 1fr;
    align-items: flex-start;
  }
  .enquiry-card__action {
    grid-column: 2;
    justify-self: end;
  }
  .enquiry-card__media img {
    width: 56px;
    height: 56px;
  }
}

@media (max-width: 576px) {
  .page-bar {
    padding: 0.85rem;
  }
  .enquiry-card {
    grid-template-columns: 1fr;
  }
  .enquiry-card__media {
    justify-self: start;
  }
  .enquiry-card__action {
    grid-column: 1;
    justify-self: end;
  }
  .enquiry-card__title h4 {
    font-size: 0.98rem;
  }
  .enquiry-card__desc {
    -webkit-line-clamp: 3;
  }
  .enquiry-card__action {
    font-size: 1rem;
  }
  .vendor-card {
    flex-direction: row;
    align-items: flex-start;
  }
  .vendor-card img {
    width: 50px;
    height: 50px;
  }
}

@keyframes shimmer {
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
